import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../hooks/useAuth";
import {
  getUserTeams,
  getInvoicesForReport,
  formatCurrency,
  formatDate,
  getCurrentFactorValue,
} from "../services/firebaseService";
import { Team, Invoice, DateRange } from "../types";
import "./ByCodeReport.css";

interface ServiceLineData {
  planilha: string;
  pedido: string;
  data: string;
  codigo: string;
  servico: string;
  quantidade: number;
  valor: number;
  projeto: string;
  municipio: string;
  si: string;
  os: string;
  setor: string;
  totalParcial: number;
}

const ByCodeReport: React.FC = () => {
  const { user } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<
    "codigo" | "data" | "quantidade" | "valor"
  >("codigo");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: "",
    endDate: "",
  });

  const loadUserTeams = async () => {
    try {
      const userTeams = await getUserTeams(user!.uid);
      setTeams(userTeams);
      setSelectedTeams([]);
    } catch (error) {
      console.error("Error loading teams:", error);
    }
  };

  useEffect(() => {
    if (user) {
      loadUserTeams();
      setInitialDateRange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const setInitialDateRange = () => {
    const currentDay = new Date().getDate();
    const currentDate = new Date();
    const lastMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() - 1,
    );
    const nextMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
    );

    let startDate, endDate;

    if (currentDay < 26) {
      startDate = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, "0")}-26`;
      endDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-25`;
    } else {
      startDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-26`;
      endDate = `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, "0")}-25`;
    }

    setDateRange({ startDate, endDate });
  };

  const loadReportData = async () => {
    if (
      selectedTeams.length === 0 ||
      !dateRange.startDate ||
      !dateRange.endDate
    ) {
      alert("Selecione pelo menos uma equipe e defina o período.");
      return;
    }

    setLoading(true);
    try {
      const allInvoices: Invoice[] = [];

      for (const teamId of selectedTeams) {
        const teamInvoices = await getInvoicesForReport(
          teamId,
          dateRange.startDate,
          dateRange.endDate,
        );
        allInvoices.push(...teamInvoices);
      }

      setInvoices(allInvoices);
    } catch (error) {
      console.error("Error loading report data:", error);
      alert("Erro ao carregar dados do relatório.");
    } finally {
      setLoading(false);
    }
  };

  const serviceLineData = useMemo(() => {
    const lineData: ServiceLineData[] = [];

    invoices.forEach((invoice) => {
      if (invoice.services && Array.isArray(invoice.services)) {
        invoice.services.forEach((service) => {
          if (service && service.code) {
            const quantity = parseFloat(service.quantity?.toString() || "0");
            const price = parseFloat(service.price?.toString() || "0");
            const parsed = parseFloat(service.factor?.toString() || "");
            const factor = !isNaN(parsed) && parsed > 0 ? parsed : getCurrentFactorValue();
            const value = quantity * price;
            const totalParcial = value * factor;

            lineData.push({
              planilha: invoice.team || "",
              pedido: invoice.id || "",
              data: formatDate(invoice.date) || "",
              codigo: service.code,
              servico: service.description || service.activity || "",
              quantidade: quantity,
              valor: price,
              projeto: invoice.project || "",
              municipio: invoice.city || "",
              si: invoice.pms || "",
              os: invoice.os || "",
              setor: invoice.sector || invoice.title || "",
              totalParcial: totalParcial,
            });
          }
        });
      }
    });

    return lineData;
  }, [invoices]);

  const filteredAndSortedData = useMemo(() => {
    let filtered = serviceLineData.filter(
      (item) =>
        searchTerm === "" ||
        item.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.servico.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.pedido.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.projeto.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.municipio.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    return filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "codigo":
          aValue = a.codigo;
          bValue = b.codigo;
          break;
        case "data":
          aValue = a.data;
          bValue = b.data;
          break;
        case "quantidade":
          aValue = a.quantidade;
          bValue = b.quantidade;
          break;
        case "valor":
          aValue = a.totalParcial;
          bValue = b.totalParcial;
          break;
        default:
          aValue = a.codigo;
          bValue = b.codigo;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortOrder === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return sortOrder === "asc"
          ? (aValue as number) - (bValue as number)
          : (bValue as number) - (aValue as number);
      }
    });
  }, [serviceLineData, searchTerm, sortBy, sortOrder]);

  const reportSummary = useMemo(() => {
    const totalLines = filteredAndSortedData.length;
    const totalQuantity = filteredAndSortedData.reduce(
      (sum, item) => sum + item.quantidade,
      0,
    );
    const totalValue = filteredAndSortedData.reduce(
      (sum, item) => sum + item.totalParcial,
      0,
    );
    const uniqueCodes = new Set(
      filteredAndSortedData.map((item) => item.codigo),
    ).size;
    const uniqueInvoices = new Set(
      filteredAndSortedData.map((item) => item.pedido),
    ).size;

    return {
      totalLines,
      totalQuantity,
      totalValue,
      uniqueCodes,
      uniqueInvoices,
    };
  }, [filteredAndSortedData]);

  const handleTeamToggle = (teamId: string) => {
    setSelectedTeams((prev) =>
      prev.includes(teamId)
        ? prev.filter((id) => id !== teamId)
        : [...prev, teamId],
    );
  };

  const handleSelectAllTeams = () => {
    setSelectedTeams(teams.map((team) => team.id));
  };

  const handleDeselectAllTeams = () => {
    setSelectedTeams([]);
  };

  const handleSort = (column: "codigo" | "data" | "quantidade" | "valor") => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const exportToCSV = () => {
    const headers = [
      "Planilha",
      "Pedido",
      "Data",
      "Código",
      "Serviço",
      "Qtd",
      "Valor",
      "Projeto",
      "Mun.",
      "SI",
      "OS",
      "Setor",
      "Total Parc.",
    ].join(";");

    const csvData = filteredAndSortedData
      .map((item) =>
        [
          `"${item.planilha}"`,
          item.pedido,
          item.data,
          item.codigo,
          `"${item.servico}"`,
          item.quantidade.toFixed(2),
          item.valor.toFixed(2),
          `"${item.projeto}"`,
          `"${item.municipio}"`,
          `"${item.si}"`,
          `"${item.os}"`,
          `"${item.setor}"`,
          item.totalParcial.toFixed(2),
        ].join(";"),
      )
      .join("\n");

    const csvContent = [headers, csvData].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `relatorio-por-codigo-${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getSortIcon = (column: string) => {
    if (sortBy !== column) return <i className="fas fa-sort"></i>;
    return sortOrder === "asc" ? (
      <i className="fas fa-sort-up"></i>
    ) : (
      <i className="fas fa-sort-down"></i>
    );
  };

  return (
    <div className="bycode-report-container">
      <div className="report-header">
        <div className="header-content">
          <h1>Relatório Por Código</h1>
          <button onClick={() => window.close()} className="close-btn">
            <i className="fas fa-times"></i>
            Fechar
          </button>
        </div>
      </div>

      <div className="report-content">
        {/* Filters Section */}
        <div className="filters-section">
          <div className="filter-group">
            <div className="teams-header">
              <h3>Equipes</h3>
              <div className="team-controls">
                <button onClick={handleSelectAllTeams} className="control-btn">
                  Selecionar Todas
                </button>
                <button
                  onClick={handleDeselectAllTeams}
                  className="control-btn"
                >
                  Desmarcar Todas
                </button>
              </div>
            </div>
            <div className="teams-grid">
              {teams.map((team) => (
                <label key={team.id} className="team-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedTeams.includes(team.id)}
                    onChange={() => handleTeamToggle(team.id)}
                  />
                  {team.name}
                </label>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <h3>Período</h3>
            <div className="date-inputs">
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) =>
                  setDateRange((prev) => ({
                    ...prev,
                    startDate: e.target.value,
                  }))
                }
                className="date-input"
              />
              <span>até</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) =>
                  setDateRange((prev) => ({ ...prev, endDate: e.target.value }))
                }
                className="date-input"
              />
            </div>
          </div>

          <div className="filter-group">
            <h3>Filtros</h3>
            <div className="filter-controls">
              <input
                type="text"
                placeholder="Pesquisar código, serviço, pedido, projeto..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          <div className="action-buttons">
            <button
              onClick={loadReportData}
              disabled={loading || selectedTeams.length === 0}
              className="load-btn"
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Carregando...
                </>
              ) : (
                <>
                  <i className="fas fa-search"></i>
                  Gerar Relatório
                </>
              )}
            </button>

            {serviceLineData.length > 0 && (
              <button onClick={exportToCSV} className="export-btn">
                <i className="fas fa-download"></i>
                Exportar CSV
              </button>
            )}
          </div>
        </div>

        {/* Summary Section */}
        {serviceLineData.length > 0 && (
          <div className="summary-section">
            <h3>Resumo</h3>
            <div className="summary-cards">
              <div className="summary-card">
                <div className="card-value">{reportSummary.totalLines}</div>
                <div className="card-label">Linhas de Serviço</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{reportSummary.uniqueCodes}</div>
                <div className="card-label">Códigos Únicos</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{reportSummary.uniqueInvoices}</div>
                <div className="card-label">Boletins Únicos</div>
              </div>
              <div className="summary-card">
                <div className="card-value">
                  {reportSummary.totalQuantity.toFixed(2)}
                </div>
                <div className="card-label">Quantidade Total</div>
              </div>
              <div className="summary-card">
                <div className="card-value">
                  {formatCurrency(reportSummary.totalValue)}
                </div>
                <div className="card-label">Valor Total</div>
              </div>
            </div>
          </div>
        )}

        {/* Results Table */}
        {serviceLineData.length > 0 && (
          <div className="results-section">
            <div className="results-header">
              <h3>Resultados ({filteredAndSortedData.length} linhas)</h3>
            </div>

            <div className="table-wrapper">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Planilha</th>
                    <th>Pedido</th>
                    <th
                      onClick={() => handleSort("data")}
                      className="sortable-header"
                    >
                      Data {getSortIcon("data")}
                    </th>
                    <th
                      onClick={() => handleSort("codigo")}
                      className="sortable-header"
                    >
                      Código {getSortIcon("codigo")}
                    </th>
                    <th>Serviço</th>
                    <th
                      onClick={() => handleSort("quantidade")}
                      className="sortable-header"
                    >
                      Qtd {getSortIcon("quantidade")}
                    </th>
                    <th>Valor</th>
                    <th>Projeto</th>
                    <th>Mun.</th>
                    <th>PES</th>
                    <th>OS</th>
                    <th>Setor</th>
                    <th
                      onClick={() => handleSort("valor")}
                      className="sortable-header"
                    >
                      Total Parc. {getSortIcon("valor")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedData.map((item, index) => (
                    <tr
                      key={`${item.pedido}-${item.codigo}-${index}`}
                      className={index % 2 === 0 ? "even-row" : "odd-row"}
                    >
                      <td className="text-cell" title={item.planilha}>
                        {item.planilha}
                      </td>
                      <td className="text-cell">{item.pedido}</td>
                      <td className="text-cell">{item.data}</td>
                      <td className="code-cell">
                        <strong>{item.codigo}</strong>
                      </td>
                      <td className="text-cell" title={item.servico}>
                        {item.servico}
                      </td>
                      <td className="number-cell">
                        {item.quantidade.toFixed(2)}
                      </td>
                      <td className="value-cell">
                        {formatCurrency(item.valor)}
                      </td>
                      <td className="text-cell" title={item.projeto}>
                        {item.projeto}
                      </td>
                      <td className="text-cell" title={item.municipio}>
                        {item.municipio}
                      </td>
                      <td className="text-cell" title={item.si}>
                        {item.si}
                      </td>
                      <td className="text-cell" title={item.os}>
                        {item.os}
                      </td>
                      <td className="text-cell" title={item.setor}>
                        {item.setor}
                      </td>
                      <td className="value-cell">
                        <strong>{formatCurrency(item.totalParcial)}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredAndSortedData.length === 0 &&
              serviceLineData.length > 0 && (
                <div className="empty-results">
                  <i className="fas fa-search"></i>
                  <p>Nenhuma linha encontrada com os filtros aplicados.</p>
                </div>
              )}
          </div>
        )}

        {serviceLineData.length === 0 && !loading && (
          <div className="no-data">
            <i className="fas fa-code"></i>
            <h3>Nenhum dado carregado</h3>
            <p>
              Configure os filtros e clique em "Gerar Relatório" para visualizar
              os códigos de serviço.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ByCodeReport;
